import { useHasPermissions } from "./use-has-permissions";

export const useHaveCustomPermissions = () => {
  const { hasPermissions: hasPermissionCreateUserReports } = useHasPermissions({
    entity: "report",
    action: "create_user_reports",
  });

  const { hasPermissions: hasPermissionCreateCompanyReports } =
    useHasPermissions({
      entity: "report",
      action: "create_company_reports",
    });
  const { hasPermissions: hasPermissionCreateGlobalReports } =
    useHasPermissions({
      entity: "report",
      action: "create_global_reports",
    });

  const { hasPermissions: hasPermissionDontSeeRideChangesPopup } =
    useHasPermissions({
      entity: "ride",
      action: "dont_see_ride_changes_popup",
    });

  const { hasPermissions: hasPermissionViewPrices } = useHasPermissions({
    entity: "system",
    action: "view_prices",
  });

  const { hasPermissions: hasPermissionViewSupplierPrices } = useHasPermissions(
    {
      entity: "system",
      action: "view_supplier_prices",
    },
  );

  const { hasPermissions: hasPermissionReadSupplierPriceRate } =
    useHasPermissions({
      entity: "supplierPriceRate",
      action: "read",
    });

  const { hasPermissions: hasPermissionEditRide } = useHasPermissions({
    entity: "ride",
    action: "update",
  });

  const { hasPermissions: hasPermissionViewRide } = useHasPermissions({
    entity: "ride",
    action: "read",
  });

  const { hasPermissions: hasPermissionCreateStation } = useHasPermissions({
    entity: "station",
    action: "create",
  });

  const { hasPermissions: hasPermissionEditStation } = useHasPermissions({
    entity: "station",
    action: "update",
  });

  const { hasPermissions: hasPermissionViewStation } = useHasPermissions({
    entity: "station",
    action: "read",
  });

  const { hasPermissions: hasPermissionCreatePassenger } = useHasPermissions({
    entity: "passenger",
    action: "create",
  });
  const { hasPermissions: hasPermissionEditPassenger } = useHasPermissions({
    entity: "passenger",
    action: "update",
  });

  const { hasPermissions: hasPermissionViewPassenger } = useHasPermissions({
    entity: "passenger",
    action: "read",
  });

  const { hasPermissions: hasPassengerSendMessage } = useHasPermissions({
    entity: "passenger",
    action: "send_message",
  });

  const { hasPermissions: hasRideUpdatePermission } = useHasPermissions({
    entity: "ride",
    action: "update",
  });
  const { hasPermissions: hasRideUpdateDriverPermission } = useHasPermissions({
    entity: "ride",
    action: "update_driver",
  });
  const { hasPermissions: hasRideUpdateVehiclePermission } = useHasPermissions({
    entity: "ride",
    action: "update_vehicle",
  });

  const { hasPermissions: readProductionPermission } = useHasPermissions({
    entity: "rideProduction",
    action: "read",
  });
  const { hasPermissions: viewLogsProductionPermission } = useHasPermissions({
    entity: "rideProduction",
    action: "view_logs",
  });

  const { hasPermissions: hasEditNewOnlyRidePermission } = useHasPermissions({
    entity: "ride",
    action: "edit_new_only",
  });

  const { hasPermissions: hasReadSystemParamsPermission } = useHasPermissions({
    entity: "systemParam",
    action: "read",
  });
  const { hasPermissions: hasReadStationRoutePermission } = useHasPermissions({
    entity: "stationRoute",
    action: "read",
  });

  const { hasPermissions: hasPermissionEditCourse } = useHasPermissions({
    entity: "course",
    action: "update",
  });

  const { hasPermissions: hasPermissionViewCourse } = useHasPermissions({
    entity: "course",
    action: "read",
  });

  const { hasPermissions: hasPermissionCreateBooking } = useHasPermissions({
    entity: "booking",
    action: "create",
  });

  const { hasPermissions: hasPermissionEditBooking } = useHasPermissions({
    entity: "booking",
    action: "update",
  });

  const { hasPermissions: hasPermissionViewBooking } = useHasPermissions({
    entity: "booking",
    action: "read",
  });

  const { hasPermissions: hasPermissionViewFeedback } = useHasPermissions({
    entity: "feedback",
    action: "read",
  });
  const { hasPermissions: hasPermissionUpdateFeedback } = useHasPermissions({
    entity: "feedback",
    action: "update",
  });
  const { hasPermissions: hasPermissionCreateFeedback } = useHasPermissions({
    entity: "feedback",
    action: "create",
  });
  const { hasPermissions: hasAdminPermissionOnFeedback } = useHasPermissions({
    entity: "feedback",
    action: "act_as_admin",
  });
  const { hasPermissions: hasSupplierPermissionOnFeedback } = useHasPermissions(
    {
      entity: "feedback",
      action: "act_as_supplier",
    },
  );
  const { hasPermissions: hasOperatorPermissionOnFeedback } = useHasPermissions(
    {
      entity: "feedback",
      action: "act_as_operator",
    },
  );

  const { hasPermissions: hasPermissionAllowSendBelowMinimum } =
    useHasPermissions({
      entity: "ride",
      action: "allow_send_below_minimum",
    });

  const hasOnlyRideDriverAndVehicleUpdatePermission =
    !hasRideUpdatePermission &&
    (hasRideUpdateDriverPermission || hasRideUpdateVehiclePermission);

  const canOpenPassengerCard = Boolean(
    hasPermissionEditPassenger || hasPermissionViewPassenger,
  );
  const canOpenStationCard = Boolean(
    hasPermissionEditPassenger || hasPermissionViewPassenger,
  );

  const canOpenProductionDrawer = Boolean(
    readProductionPermission || viewLogsProductionPermission,
  );

  const canOpenCourseDrawer = Boolean(
    hasPermissionEditCourse || hasPermissionViewCourse,
  );

  const canViewBooking = Boolean(hasPermissionViewBooking);
  const canEditBooking = Boolean(hasPermissionEditBooking);
  const canEditStation = Boolean(hasPermissionEditStation);

  return {
    hasPermissionCreateUserReports,
    hasPermissionCreateCompanyReports,
    hasPermissionCreateGlobalReports,
    hasPermissionDontSeeRideChangesPopup,
    hasPermissionViewPrices,
    hasPermissionViewSupplierPrices,
    hasPermissionReadSupplierPriceRate,
    hasPermissionEditRide,
    hasPermissionViewRide,
    hasPermissionCreateStation,
    hasPermissionEditStation,
    hasPermissionViewStation,
    hasPermissionCreatePassenger,
    hasPermissionEditPassenger,
    hasPermissionViewPassenger,
    hasPassengerSendMessage,
    hasOnlyRideDriverAndVehicleUpdatePermission,
    hasRideUpdatePermission,
    hasRideUpdateDriverPermission,
    hasRideUpdateVehiclePermission,
    canOpenPassengerCard,
    canOpenStationCard,
    readProductionPermission,
    viewLogsProductionPermission,
    canOpenProductionDrawer,
    hasEditNewOnlyRidePermission,
    hasReadSystemParamsPermission,
    hasReadStationRoutePermission,
    hasPermissionEditCourse,
    hasPermissionViewCourse,
    canOpenCourseDrawer,
    hasPermissionCreateBooking,
    hasPermissionEditBooking,
    hasPermissionViewBooking,
    canViewBooking,
    canEditBooking,
    canEditStation,
    hasPermissionViewFeedback,
    hasPermissionUpdateFeedback,
    hasPermissionCreateFeedback,
    hasSupplierPermissionOnFeedback,
    hasAdminPermissionOnFeedback,
    hasOperatorPermissionOnFeedback,
    hasPermissionAllowSendBelowMinimum,
  };
};
